import type * as React from "react";
import { type ChangeEvent, type FC, type ReactNode, useMemo } from "react";

import Spacer from "~/common/components/Spacer";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import { type WishlistModel } from "~/features/wishlist/types/model";
import dayjs from "dayjs";
import { DateFormatType, formatDate } from "~/common/utils/DateFormatter";
import { type DropdownEntry } from "~/common/types";
import { Button, styled } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { C_PRIMARY, C_PRIMARY_DISABLED } from "~/common/components/icon/Colors";
import AddIcon from "@mui/icons-material/Add";

const OptionText = styled("span")({
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const OptionCount = styled("span")(({ theme }) => ({
  color: C_PRIMARY_DISABLED,
  marginLeft: theme.spacing(0.625),
  paddingRight: theme.spacing(1),
  [theme.breakpoints.up("tablet")]: {
    color: C_PRIMARY_DISABLED,
  },
  "& [aria-selected='true'] > div > &": {
    color: C_PRIMARY,
  },
}));

interface WishlistDropdownProps {
  className?: string;
  wishlists: WishlistModel[];
  selectedWishlist?: string | null;
  onWishlistSelect: (wishlistId?: string) => void;
  onNewWishlist?: () => void;
  disabled?: boolean;

  label?: ReactNode;
  error?: boolean;
}

const WishlistDropdown: FC<WishlistDropdownProps> = (props) => {
  const { className, wishlists, selectedWishlist, onWishlistSelect, disabled } = props;
  const { val: dropdownStatus, setTrue: open, setFalse: close } = useBooleanState(false);
  const { t } = useTranslation();
  const onSelect = (e: ChangeEvent<HTMLInputElement>) => {
    // @ts-ignore
    if (!e.isAdd) {
      onWishlistSelect(e.target.value as string);
    }
  };

  const onAdd = (e: React.MouseEvent) => {
    // @ts-ignore
    e.isAdd = true;
    props.onNewWishlist?.();
    close();
  };

  const wishlistEntries: DropdownEntry<string>[] = useMemo(() => {
    return wishlists.map((wishlist) => {
      const name = wishlist.name;
      const date = dayjs.utc(wishlist.lastModification).toDate();
      const lastModified = formatDate(date, DateFormatType.DATE);

      return {
        value: wishlist.id,
        label: (
          <div className="d-flex flex-justify-between w-100">
            <OptionText>
              {t("wishlistDropdown.entry", { name, lastModified })}
            </OptionText>
            <OptionCount>({wishlist.entryIds.length})</OptionCount>
          </div>
        ),
      };
    });
  }, [wishlists, t]);

  const label = props.label ?? t("wishlistPage.dropdownLabel");

  return (
    <SimpleSelect
      fullWidth={true}
      values={wishlistEntries}
      value={selectedWishlist}
      label={label}
      className={className}
      data-test-id={"dropdown-select-wishlist"}
      onChange={onSelect}
      disabled={disabled}
      error={props.error}
      onClose={close}
      onOpen={open}
      open={dropdownStatus}
    >
      {wishlistEntries.length < 1 && (
        <div className="p-2 f-label-color">{t("wishlist.dropdown.noWishlistMessage")}</div>
      )}
      {props.onNewWishlist && (
        <>
          <Spacer className={"mt-2 mb-0"} />
          <Button
            startIcon={<AddIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />}
            data-test-id={"dropdown-select-wishlist-btn-new-wishlist"}
            fullWidth={true}
            onClick={onAdd}
            variant="text"
          >
            {t("wishlistDropdown.add")}
          </Button>
        </>
      )}
    </SimpleSelect>
  );
};

export default WishlistDropdown;
